import React from 'react';
import { Camera, Sparkles, HeartHandshake, ShieldCheck } from 'lucide-react';
import { useSpotlight } from '../../hooks/useSpotlight';

const STEPS = [
    {
        n: '01',
        color: '#0393E7',
        title: 'What we set out to solve',
        body: 'Logging every meal takes effort. Fork removes that step by understanding a single photo.',
    },
    {
        n: '02',
        color: '#02F3A2',
        title: 'How we help',
        body: 'We break down macros, give a nutrition score, and surface quick takeaways you can act on.',
    },
    {
        n: '03',
        color: '#DDDD61',
        title: 'Where we shine',
        body: 'Daily usability — fast capture, smart defaults, and screens that make sense at a glance.',
    },
];

const VALUES = [
    {
        icon: Camera,
        chip: '#FFD6D7',
        title: 'Photo-first tracking',
        body: 'Capture meals the way you live — quick snaps or voice. No barcode hunts or manual databases.',
    },
    {
        icon: Sparkles,
        chip: '#FFE7CE',
        title: 'Clarity over hype',
        body: 'Every score comes with a note you can act on, so the numbers actually mean something.',
    },
    {
        icon: HeartHandshake,
        chip: '#F1DFFF',
        title: 'Built for humans',
        body: 'Guidance that respects busy days and imperfect meals — small, encouraging nudges.',
    },
    {
        icon: ShieldCheck,
        chip: '#F9FFCC',
        title: 'Privacy aware',
        body: 'Your meals are your business. We design with consent and data minimization in mind.',
    },
];

/** Value card: lifts 6px on hover, spotlight painted over #202428. */
const ValueCard: React.FC<{ value: (typeof VALUES)[number] }> = ({ value }) => {
    const attachSpotlight = useSpotlight<HTMLDivElement>('#202428');
    return (
        <div
            ref={attachSpotlight}
            className="relative rounded-[20px] bg-[#202428] p-6 transition-[transform,box-shadow] duration-[380ms] ease-[cubic-bezier(.22,1,.36,1)] hover:-translate-y-1.5 hover:shadow-[0_20px_40px_rgba(0,0,0,0.4)]"
        >
            <span
                className="mb-5 flex h-11 w-11 items-center justify-center rounded-full text-dark-base"
                style={{ background: value.chip }}
            >
                <value.icon className="h-[21px] w-[21px]" strokeWidth={1.8} />
            </span>
            <h4 className="mb-2 text-[18px] font-bold tracking-[-0.01em] text-white">
                {value.title}
            </h4>
            <p className="text-[15px] leading-[1.6] text-dark-textSecondary">{value.body}</p>
        </div>
    );
};

const AboutSection: React.FC = () => {
    return (
        <section
            id="about"
            className="relative z-[1] scroll-mt-24 border-t border-dark-gunmetal px-5 py-[clamp(64px,9vw,120px)]"
        >
            <div className="mx-auto max-w-[1180px]">
                <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-success">
                    Why we built Fork
                </span>
                <p className="mt-[18px] max-w-[20ch] text-[clamp(28px,4.2vw,54px)] font-extrabold leading-[1.04] tracking-[-0.035em] text-white [text-wrap:balance]">
                    Nutrition tracking that fits your life
                </p>

                <div className="mt-[clamp(32px,4vw,56px)] grid items-stretch gap-[clamp(16px,2.4vw,20px)] lg:grid-cols-[minmax(280px,0.86fr)_minmax(320px,1.14fr)]">
                    {/* lifestyle image with a glass pull-quote */}
                    <div className="relative min-h-[420px] overflow-hidden rounded-3xl">
                        <img
                            src="/assets/lifestyle/lifestyle-1.webp"
                            alt=""
                            aria-hidden="true"
                            loading="lazy"
                            className="absolute inset-0 h-full w-full object-cover"
                        />
                        <span
                            aria-hidden="true"
                            className="absolute inset-0 bg-[linear-gradient(200deg,rgba(15,19,22,0)_40%,rgba(15,19,22,0.9)_100%)]"
                        />
                        <div className="absolute inset-x-5 bottom-5 rounded-2xl border border-white/10 bg-[rgba(15,19,22,0.62)] p-[18px] backdrop-blur-[20px]">
                            <p className="text-[17px] font-semibold leading-[1.5] tracking-[-0.01em] text-white">
                                "A meal should take a second to log, not a spreadsheet."
                            </p>
                        </div>
                    </div>

                    <div className="grid min-w-0 gap-3">
                        <p className="mb-2 mt-1 max-w-[52ch] text-[clamp(17px,1.6vw,20px)] leading-[1.6] text-dark-textSecondary [text-wrap:pretty]">
                            We built Fork around how people actually eat. Computer vision reads the
                            plate, so a photo is enough to get a clear, honest breakdown — then you get
                            on with your day.{' '}
                            <span className="font-semibold text-white">
                                No calorie math, no guesswork.
                            </span>
                        </p>

                        {/* numbered badges joined by a blue -> mint connector */}
                        <div className="relative grid gap-0 py-1">
                            <div
                                aria-hidden="true"
                                className="absolute bottom-[26px] left-[19px] top-[26px] w-px bg-[linear-gradient(180deg,#0393E7,#02F3A2)]"
                            />
                            {STEPS.map((step) => (
                                <div key={step.n} className="relative flex gap-4 py-3.5">
                                    <span
                                        className="z-[1] grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#202428] text-[13px] font-extrabold"
                                        style={{
                                            border: `2px solid ${step.color}`,
                                            color: step.color,
                                        }}
                                    >
                                        {step.n}
                                    </span>
                                    <div className="pt-1.5">
                                        <p className="mb-1 text-base font-bold text-white">
                                            {step.title}
                                        </p>
                                        <p className="text-[15px] leading-[1.55] text-dark-textSecondary">
                                            {step.body}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* value cards */}
                <div className="mt-[clamp(40px,5vw,72px)] grid gap-3.5 px-0.5 py-1.5 sm:grid-cols-2 lg:grid-cols-4">
                    {VALUES.map((value) => (
                        <ValueCard key={value.title} value={value} />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default AboutSection;
